<template>
  <div v-if="glyph" class="rail-glyph" aria-hidden="true">
    <component :is="glyph.component" :class="['glyph', positionClass]" :style="glyphStyle" />
  </div>
</template>

<script setup lang="ts">

import { computed, Component } from 'vue';
import { RailGlyphPosition, RailGlyphType, RailResponse } from '@/types/cms';

import GlyphT from './GlyphT.vue';
import GlyphG from './GlyphG.vue';
import GlyphLeftX from './GlyphLeftX.vue';
import GlyphRightX from './GlyphRightX.vue';
import GlyphGRotatedRight from './GlyphGRotatedRight.vue';
import GlyphCRotatedLeft from './GlyphCRotatedLeft.vue';


const GLYPH_SCALE = 4;


const GLYPHS: Record<RailGlyphType, { component: Component; width: number; height: number }> = {
  T: { component: GlyphT, width: 304, height: 285 },
  G: { component: GlyphG, width: 192, height: 128 },
  LEFT_X: { component: GlyphLeftX, width: 85, height: 200 },
  RIGHT_X: { component: GlyphRightX, width: 85, height: 200 },
  G_ROTATED_RIGHT: { component: GlyphGRotatedRight, width: 88, height: 200 },
  C_ROTATED_LEFT: { component: GlyphCRotatedLeft, width: 144, height: 200 },
};

const DEFAULT_POSITION: RailGlyphPosition = 'TOP_MIDDLE';

const props = defineProps<{
  rail: RailResponse;
}>();

const glyph = computed(() => {
  const type = props.rail.backgroundGlyph;
  if (!type) {
    return null;
  }

  return GLYPHS[type] ?? null;
});

const positionClass = computed(() => {
  const position = props.rail.backgroundGlyphPosition ?? DEFAULT_POSITION;
  return `at-${position.toLowerCase().replace(/_/g, '-')}`;
});

const glyphStyle = computed(() => {
  if (!glyph.value) {
    return undefined;
  }
  return {
    width: `${glyph.value.width * GLYPH_SCALE}px`,
    height: `${glyph.value.height * GLYPH_SCALE}px`,
    color: props.rail.backgroundGlyphColor ?? undefined,
  };
});

</script>

<style scoped lang="scss">
.rail-glyph {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
}

.glyph {
  position: absolute;
  display: block;
  color: $color-pink;
}


.at-top-left {
  top: 0;
  left: 0;
}

.at-top-middle {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.at-top-right {
  top: 0;
  right: 0;
}

.at-middle-left {
  top: 50%;
  left: 0;
  transform: translateY(-50%);
}

.at-middle-right {
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.at-bottom-left {
  bottom: 0;
  left: 0;
}

.at-bottom-middle {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.at-bottom-right {
  bottom: 0;
  right: 0;
}
</style>
